<script>
import { useAlert } from 'dashboard/composables';

export default {
  name: 'AceleresConfirmDeleteCannedModal',
  props: {
    show: { type: Boolean, default: false },
    cannedResponse: { type: Object, default: null },
  },
  emits: ['close', 'deleted'],
  data() {
    return { deleting: false };
  },
  watch: {
    show(val) {
      if (val) {
        document.addEventListener('keydown', this.onEsc);
      } else {
        document.removeEventListener('keydown', this.onEsc);
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onEsc);
  },
  methods: {
    onEsc(e) {
      if (e.key === 'Escape' && !this.deleting) this.onCancel();
    },
    onCancel() {
      this.$emit('close');
    },
    async onConfirm() {
      if (!this.cannedResponse?.id || this.deleting) return;
      this.deleting = true;
      try {
        await this.$store.dispatch(
          'deleteCannedResponse',
          this.cannedResponse.id
        );
        useAlert('Resposta rápida excluída com sucesso.');
        this.$emit('deleted');
        this.$emit('close');
      } catch (e) {
        // throwErrorMessage já mostra o erro do backend.
      } finally {
        this.deleting = false;
      }
    },
    preview(text) {
      const s = (text || '').replace(/\s+/g, ' ');
      return s.length > 120 ? s.slice(0, 120) + '…' : s;
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="acl-del-backdrop fixed inset-0 z-[2147483600] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4 backdrop-blur-[2px]"
      @click.self="onCancel"
    >
      <div
        class="acl-del-modal"
        role="alertdialog"
        aria-label="Confirmar exclusão"
      >
        <header class="acl-del-header">
          <div class="acl-del-icon">
            <i class="i-ph-warning-circle" />
          </div>
          <span class="acl-del-title">Excluir resposta rápida?</span>
        </header>

        <div class="acl-del-body">
          <p class="acl-del-text">
            Tem certeza que deseja excluir
            <strong v-if="cannedResponse?.short_code">
              /{{ cannedResponse.short_code }}
            </strong>
            <strong v-else>esta resposta rápida</strong>?
          </p>
          <p v-if="cannedResponse?.content" class="acl-del-preview">
            “{{ preview(cannedResponse.content) }}”
          </p>
          <p class="acl-del-note">Esta ação não pode ser desfeita.</p>
        </div>

        <footer class="acl-del-footer">
          <button
            type="button"
            class="acl-del-btn acl-del-btn-secondary"
            :disabled="deleting"
            @click="onCancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="acl-del-btn acl-del-btn-danger"
            :disabled="deleting"
            @click="onConfirm"
          >
            {{ deleting ? 'Excluindo…' : 'OK, excluir' }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.acl-del-modal {
  width: 100%;
  max-width: 520px;
  background: var(--white, #ffffff);
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.acl-del-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px 10px;
}

.acl-del-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(220, 38, 38, 0.12);
  color: var(--r-600, #dc2626);

  i { font-size: 24px; }
}

.acl-del-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--s-800, #1e293b);
}

.acl-del-body {
  padding: 6px 24px 20px;
}

.acl-del-text {
  margin: 0 0 12px;
  font-size: 16px;
  color: var(--s-700, #334155);
  line-height: 1.5;

  strong {
    color: var(--s-800, #1e293b);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-weight: 600;
  }
}

.acl-del-preview {
  margin: 0 0 12px;
  padding: 12px 14px;
  background: var(--s-50, #f8fafc);
  border-left: 4px solid var(--s-300, #cbd5e1);
  border-radius: 6px;
  font-size: 14.5px;
  color: var(--s-600, #475569);
  line-height: 1.5;
}

.acl-del-note {
  margin: 0;
  font-size: 13.5px;
  color: var(--s-500, #64748b);
}

.acl-del-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--s-100, #e2e8f0);
  background: var(--s-25, #fafbfc);
}

.acl-del-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.12s ease, color 0.12s ease;

  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

.acl-del-btn-secondary {
  background: transparent;
  border-color: var(--s-200, #e2e8f0);
  color: var(--s-700, #334155);

  &:hover:not(:disabled) {
    background: var(--s-50, #f8fafc);
    border-color: var(--s-300, #cbd5e1);
  }
}

.acl-del-btn-danger {
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;

  &:hover:not(:disabled) { background: #b91c1c; border-color: #b91c1c; }
}
</style>
